import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge, Button, Container, Divider, Group, Modal, Paper, SegmentedControl, Select, Stack, Text, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCamera, IconCertificate, IconClipboardText, IconEdit, IconFileSpreadsheet, IconLink,
  IconPlus, IconPrinter, IconReload, IconTable, IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { Customer, DeliveryType, Shipment } from "../api/types";
import { localAgentPrint } from "../api/localAgent";
import { confirmWarrantyContract } from "../lib/warranty";
import { useLocalAgent } from "../hooks/useLocalAgent";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ExportToSheetModal } from "../components/shipments/ExportToSheetModal";
import { ExportActModal } from "../components/ExportActModal";
import { PrintPassportLabelsModal } from "../components/shipments/PrintPassportLabelsModal";

const STORAGE_KEY = "station.session.v1";

interface Session {
  shipmentId: number;
  shipmentCode: string;
  customerId: number | null;
  customerName: string;
  deliveryTypeId: number | null;
  shippedDate: string | null;
  box: number;
  boxCount: number;
  totalCount: number;
  seen: string[];
}

function loadSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function MobileStation() {
  const { t } = useTranslation();
  const localAgent = useLocalAgent();

  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryType[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [manual, setManual] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);
  const [annotations, setAnnotations] = useState<Map<string, { color: string; label: string }>>(new Map());
  const processed = useRef<Set<string>>(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<string | null>(null);
  const [editType, setEditType] = useState<string | null>(null);
  const [editDate, setEditDate] = useState("");
  const [clearOpen, setClearOpen] = useState(false);
  type ScanMode = "add" | "remove" | "from_return";
  const [scanMode, setScanMode] = useState<ScanMode>("add");
  const scanModeRef = useRef<ScanMode>("add");
  useEffect(() => { scanModeRef.current = scanMode; processed.current.clear(); }, [scanMode]);

  useEffect(() => {
    void api.listCustomers().then(setCustomers).catch(() => undefined);
    void api.listDeliveryTypes(true).then(setDeliveryTypes).catch(() => undefined);
    void api.listShipments().then(setShipments).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  }, [session]);

  const sessionRef = useRef<Session | null>(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  const knownRaws = useMemo(() => new Set(session?.seen ?? []), [session]);

  async function start() {
    if (!customerId) {
      notifications.show({ color: "red", message: t("station.pickCustomer") });
      return;
    }
    const internal = customerId === "__internal__";
    setStarting(true);
    try {
      const { code } = await api.nextShipmentCode();
      const normal = deliveryTypes.find((d) => d.slug === "normal");
      const { shipment } = await api.createShipment({
        code,
        customer_id: internal ? null : Number(customerId),
        delivery_type_id: normal ? normal.id : null,
      });
      const cust = internal ? null : customers.find((c) => String(c.id) === customerId);
      setSession({
        shipmentId: shipment.id, shipmentCode: shipment.code,
        customerId: internal ? null : Number(customerId),
        customerName: internal ? t("station.internalStock") : (cust ? cust.name : ""),
        deliveryTypeId: normal ? normal.id : null,
        shippedDate: null,
        box: 1, boxCount: 0, totalCount: 0, seen: [],
      });
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200) : String(err),
      });
    } finally {
      setStarting(false);
    }
  }

  async function openExisting() {
    if (!existingId) return;
    const sh = shipments.find((s) => String(s.id) === existingId);
    if (!sh) return;
    setStarting(true);
    try {
      const devs = await api.listShipmentDevices(sh.id);
      const seen = devs.map((d) => d.dev_eui.toUpperCase());
      const boxes = devs.map((d) => Number(d.box_number)).filter((n) => Number.isFinite(n));
      const nextBox = boxes.length ? Math.max(...boxes) + 1 : 1;
      const cust = customers.find((c) => c.id === sh.customer_id);
      setSession({
        shipmentId: sh.id, shipmentCode: sh.code,
        customerId: sh.customer_id,
        customerName: sh.customer_id == null ? t("station.internalStock") : (cust ? cust.name : ""),
        deliveryTypeId: sh.delivery_type_id, shippedDate: sh.shipped_date,
        box: nextBox, boxCount: 0, totalCount: devs.length, seen,
      });
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200) : String(err),
      });
    } finally {
      setStarting(false);
    }
  }

  async function addCode(raw: string) {
    const code = raw.trim();
    if (!code) return;
    const key = code.toUpperCase();
    if (processed.current.has(key)) return;
    const s = sessionRef.current;
    if (!s) return;
    if (s.seen.includes(key)) return; // already counted (survives reload via persisted session)
    processed.current.add(key);
    try {
      const fromReturn = scanModeRef.current === "from_return";
      const res = await api.bulkAssign(s.shipmentId, String(s.box), [code], { fromReturn });
      if (res.applied.length) {
        setSession((cur) => cur && {
          ...cur, boxCount: cur.boxCount + 1, totalCount: cur.totalCount + 1,
          seen: [...cur.seen, key],
        });
      } else if (res.conflicts.length) {
        const c = res.conflicts[0];
        const label = `${c.in_shipment_code ?? "?"}${c.in_shipment_customer ? ` · ${c.in_shipment_customer}` : ""}${c.in_shipment_index ? ` #${c.in_shipment_index}` : ""}`;
        setAnnotations((m) => {
          const n = new Map(m);
          n.set(key, { color: "rgba(155, 89, 232, 1)", label });
          return n;
        });
        notifications.show({
          color: "violet",
          message: t("station.conflict", { code: c.in_shipment_code ?? "?" }),
        });
      } else {
        processed.current.delete(key); // allow retry of a not-found
        notifications.show({ color: "red", message: t("station.notFound", { code }) });
      }
    } catch (err) {
      processed.current.delete(key);
      notifications.show({ color: "red", message: String(err) });
    }
  }

  function handleDetect(raw: string) {
    if (scanModeRef.current === "remove") void removeCode(raw);
    else void addCode(raw); // "add" and "from_return" both go through addCode
  }

  async function removeCode(raw: string) {
    const code = raw.trim();
    if (!code) return;
    const key = code.toUpperCase();
    if (processed.current.has(key)) return;
    const s = sessionRef.current;
    if (!s) return;
    processed.current.add(key);
    try {
      const res = await api.lookupDevice(code);
      if (!res.found || !res.device) {
        processed.current.delete(key);
        notifications.show({ color: "red", message: t("station.notFound", { code }) });
        return;
      }
      if (res.device.shipment_id !== s.shipmentId) {
        processed.current.delete(key);
        notifications.show({ color: "orange", message: t("station.notInShipment") });
        return;
      }
      await api.bulkUpdateDevices([res.device.id], { shipment_id: null });
      setSession((cur) => cur && {
        ...cur,
        totalCount: Math.max(0, cur.totalCount - 1),
        seen: cur.seen.filter((x) => x !== key),
      });
      notifications.show({ color: "green", message: t("station.removed", { code }) });
    } catch (err) {
      processed.current.delete(key);
      notifications.show({ color: "red", message: String(err) });
    }
  }

  function openEdit() {
    const s = sessionRef.current;
    if (!s) return;
    setEditCustomer(s.customerId == null ? "__internal__" : String(s.customerId));
    setEditType(s.deliveryTypeId == null ? null : String(s.deliveryTypeId));
    setEditDate(s.shippedDate ?? "");
    setEditOpen(true);
  }

  async function saveEdit() {
    const s = sessionRef.current;
    if (!s) return;
    const internal = editCustomer === "__internal__";
    try {
      const sh = await api.updateShipment(s.shipmentId, {
        customer_id: internal ? null : (editCustomer ? Number(editCustomer) : null),
        delivery_type_id: editType ? Number(editType) : null,
        shipped_date: editDate || null,
      });
      const cust = internal ? null : customers.find((c) => c.id === sh.customer_id);
      setSession((cur) => cur && {
        ...cur,
        customerId: sh.customer_id,
        customerName: sh.customer_id == null ? t("station.internalStock") : (cust ? cust.name : ""),
        deliveryTypeId: sh.delivery_type_id,
        shippedDate: sh.shipped_date,
      });
      setEditOpen(false);
      notifications.show({ color: "green", message: t("station.saved") });
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200) : String(err),
      });
    }
  }

  async function clearAll() {
    const s = sessionRef.current;
    if (!s) return;
    try {
      const devs = await api.listShipmentDevices(s.shipmentId);
      if (devs.length) await api.bulkUpdateDevices(devs.map((d) => d.id), { shipment_id: null });
      setSession((cur) => cur && { ...cur, seen: [], totalCount: 0, boxCount: 0, box: 1 });
      processed.current.clear();
      setAnnotations(new Map());
      setClearOpen(false);
      notifications.show({ color: "green", message: t("station.clearDone", { n: devs.length }) });
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200) : String(err),
      });
    }
  }

  function newBox() {
    setSession((s) => s && { ...s, box: s.box + 1, boxCount: 0 });
  }

  function finish() {
    setSession(null);
    setCustomerId(null);
    processed.current.clear();
    setAnnotations(new Map());
  }

  function printErr(e: unknown) {
    notifications.show({
      color: "red", title: t("station.printFailed"),
      message: e instanceof ApiError ? JSON.stringify(e.detail).slice(0, 200)
        : e instanceof Error ? e.message : "",
    });
  }

  async function printPartida() {
    if (!session) return;
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintShipmentPayload(session.shipmentId, "small");
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintShipment(session.shipmentId, "small");
      }
      notifications.show({ color: "green", message: t("station.sent") });
    } catch (e) {
      printErr(e);
    }
  }

  async function printLink() {
    if (!session) return;
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintLinkPayload(session.shipmentId, "small");
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintLink(session.shipmentId, "small");
      }
      notifications.show({ color: "green", message: t("station.sent") });
    } catch (e) {
      printErr(e);
    }
  }

  function exportWarranty() {
    if (!session) return;
    if (session.customerId == null) {
      notifications.show({ color: "red", message: t("station.noCustomer") });
      return;
    }
    const cust = customers.find((c) => c.id === session.customerId);
    if (!confirmWarrantyContract(cust, t)) return;
    window.location.href = api.exportWarrantyUrl([session.shipmentId]);
  }

  function exportWarrantyPdf() {
    if (!session) return;
    if (session.customerId == null) {
      notifications.show({ color: "red", message: t("station.noCustomer") });
      return;
    }
    const cust = customers.find((c) => c.id === session.customerId);
    if (!confirmWarrantyContract(cust, t)) return;
    window.location.href = api.exportWarrantyPdfUrl([session.shipmentId]);
  }

  function exportAccounting() {
    if (session) window.location.href = api.exportShipmentUrl(session.shipmentId, { accounting: true });
  }

  // Stage 1: start
  if (!session) {
    return (
      <Container size="xs" py="md">
        <Title order={3} mb="md">{t("station.title")}</Title>
        <Paper withBorder p="md">
          <Stack>
            <Select
              label={t("station.customerLabel")}
              placeholder={t("station.customerPlaceholder")}
              data={[
                { value: "__internal__", label: t("station.internalStock") },
                ...customers.map((c) => ({ value: String(c.id), label: c.name })),
              ]}
              value={customerId}
              onChange={setCustomerId}
              searchable
              size="md"
            />
            <Button size="lg" fullWidth loading={starting} onClick={() => void start()}>
              {t("station.start")}
            </Button>
            <Divider label={t("station.or")} labelPosition="center" my="xs" />
            <Select
              label={t("station.openExisting")}
              placeholder={t("station.customerPlaceholder")}
              data={shipments.map((s) => ({ value: String(s.id), label: `${s.code} (${s.device_count})` }))}
              value={existingId}
              onChange={setExistingId}
              searchable
              size="md"
            />
            <Button size="lg" fullWidth variant="default" loading={starting} onClick={() => void openExisting()}>
              {t("station.open")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Stage 2+3: scan + actions
  const actions: { key: string; icon: React.ReactNode; onClick: () => void }[] = [
    { key: "btnPartida", icon: <IconPrinter size={20} />, onClick: () => void printPartida() },
    { key: "btnLink", icon: <IconLink size={20} />, onClick: () => void printLink() },
    { key: "btnSheets", icon: <IconTable size={20} />, onClick: () => setSheetOpen(true) },
    { key: "btnAct", icon: <IconClipboardText size={20} />, onClick: () => setActOpen(true) },
    { key: "btnWarranty", icon: <IconCertificate size={20} />, onClick: exportWarranty },
    { key: "btnWarrantyPdf", icon: <IconCertificate size={20} />, onClick: exportWarrantyPdf },
    { key: "btnAccounting", icon: <IconFileSpreadsheet size={20} />, onClick: exportAccounting },
    { key: "btnPassport", icon: <IconPrinter size={20} />, onClick: () => setPassportOpen(true) },
  ];

  return (
    <Container size="xs" py="md">
      <Group justify="space-between" mb="xs">
        <div>
          <Title order={4}>{session.shipmentCode}</Title>
          <Text size="sm" c="dimmed">{session.customerName}</Text>
        </div>
        <Badge size="lg" variant="light">{t("station.total", { n: session.totalCount })}</Badge>
      </Group>

      <Group gap="xs" mb="sm">
        <Button size="xs" variant="default" leftSection={<IconEdit size={14} />} onClick={openEdit}>
          {t("station.edit")}
        </Button>
        <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />} onClick={() => setClearOpen(true)}>
          {t("station.clearAll")}
        </Button>
      </Group>

      <Paper withBorder p="sm" mb="sm">
        <SegmentedControl
          fullWidth mb="sm" value={scanMode}
          onChange={(v) => setScanMode(v as ScanMode)}
          color={scanMode === "remove" ? "red" : scanMode === "from_return" ? "orange" : "blue"}
          data={[
            { value: "add", label: t("station.modeAdd") },
            { value: "remove", label: t("station.modeRemove") },
            { value: "from_return", label: t("station.modeFromReturn") },
          ]}
        />
        {scanMode === "from_return" && (
          <Text size="xs" c="orange" mb="xs">{t("station.fromReturnHint")}</Text>
        )}
        <Group justify="space-between" mb="xs">
          <Text fw={600}>{t("station.box", { n: session.box })}</Text>
          <Text>{t("station.inBox", { n: session.boxCount })}</Text>
        </Group>
        <Button variant="default" fullWidth leftSection={<IconPlus size={16} />} onClick={newBox} mb="sm">
          {t("station.newBox")}
        </Button>
        <Button
          variant={cameraOn ? "filled" : "default"} color={cameraOn ? "red" : "blue"} fullWidth mb="sm"
          leftSection={<IconCamera size={16} />} onClick={() => setCameraOn((v) => !v)}
        >
          {cameraOn ? t("station.cameraStop") : t("station.cameraStart")}
        </Button>
        {cameraOn && (
          <div style={{ position: "relative", width: "100%", marginBottom: 8 }}>
            <BarcodeScanner active={cameraOn} onDetect={(raw) => handleDetect(raw)} knownRaws={knownRaws} annotations={annotations} />
            <div style={{
              position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.65)",
              color: "white", padding: "4px 10px", borderRadius: 8,
              fontVariantNumeric: "tabular-nums", display: "flex", gap: 10, alignItems: "baseline",
            }}>
              <Text component="span" size="xl" fw={700} c="white" lh={1}>{session.boxCount}</Text>
              <Text component="span" size="xs" c="white" opacity={0.85}>{t("station.box", { n: session.box })}</Text>
              <Text component="span" size="xs" c="white" opacity={0.6}>/</Text>
              <Text component="span" size="md" fw={600} c="white" lh={1}>{session.totalCount}</Text>
            </div>
          </div>
        )}
        <Group gap="xs" wrap="nowrap">
          <TextInput
            style={{ flex: 1 }} placeholder={t("station.manualPlaceholder")}
            value={manual} onChange={(e) => setManual(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) { handleDetect(manual); setManual(""); } }}
          />
          <Button onClick={() => { if (manual.trim()) { handleDetect(manual); setManual(""); } }}>
            {t("station.add")}
          </Button>
        </Group>
      </Paper>

      <Stack gap="xs">
        {actions.map((a) => (
          <Button key={a.key} size="lg" fullWidth variant="light" justify="flex-start"
            leftSection={a.icon} onClick={a.onClick}>
            {t(`station.${a.key}`)}
          </Button>
        ))}
        <Button size="md" fullWidth color="gray" variant="subtle"
          leftSection={<IconReload size={16} />} onClick={finish} mt="sm">
          {t("station.finish")}
        </Button>
      </Stack>

      <ExportToSheetModal opened={sheetOpen} onClose={() => setSheetOpen(false)} target="shipments" ids={[session.shipmentId]} />
      <ExportActModal opened={actOpen} onClose={() => setActOpen(false)} shipmentIds={[session.shipmentId]} />
      <PrintPassportLabelsModal opened={passportOpen} onClose={() => setPassportOpen(false)} shipmentId={session.shipmentId} />

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t("station.editTitle")} centered>
        <Stack>
          <Select
            label={t("station.customerLabel")}
            data={[
              { value: "__internal__", label: t("station.internalStock") },
              ...customers.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
            value={editCustomer} onChange={setEditCustomer} searchable
          />
          <Select
            label={t("station.deliveryTypeLabel")}
            data={deliveryTypes.map((d) => ({ value: String(d.id), label: d.name }))}
            value={editType} onChange={setEditType} clearable
          />
          <TextInput
            label={t("station.dateLabel")} type="date"
            value={editDate} onChange={(e) => setEditDate(e.currentTarget.value)}
          />
          <Button onClick={() => void saveEdit()}>{t("station.save")}</Button>
        </Stack>
      </Modal>

      <Modal opened={clearOpen} onClose={() => setClearOpen(false)} title={t("station.clearAll")} centered>
        <Stack>
          <Text>{t("station.clearAllConfirm", { n: session.totalCount, code: session.shipmentCode })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setClearOpen(false)}>{t("station.cancel")}</Button>
            <Button color="red" onClick={() => void clearAll()}>{t("station.clearAll")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
